import React, { useCallback, useContext, useMemo, useState } from 'react'
import { IconContext } from 'react-icons'
import { FaDownload, FaSpinner } from 'react-icons/fa'
import * as XLSX from 'xlsx'

import { WidgetDataContext } from '../../../wrappers/WidgetDataContext'
import { DefaultTooltip as Tooltip } from '../../Tooltip'
import exportableWidgetTypes from '../../../../utils/constants/widgets/exportableWidgetTypes'
import { DateContext } from '../../../wrappers/DateContext'
import dayjs from 'dayjs'
import withConfig from '../../../wrappers/withConfig'
import { CurrentDashboardContext } from '../../../wrappers/CurrentDashboardContext'
import { APIRequestContext } from '../../../wrappers/APIRequestContext'
import getHiddenColumns from '../../../../utils/widgets/getHiddenColumns'

const constructDownloadName = (widget, date, exportPrefix, page) => {
    let applyDateFilter = page ? page.ApplyDateFilter : true
    let downloadName = exportPrefix
        ? `${exportPrefix}_${widget.WidgetName}`
        : `${widget.PageName}_${widget.WidgetName}`
    if (applyDateFilter) {
        if (!date.minDate) {
            const maxDateString = dayjs(date.maxDate).format('YYYYMMDD').toString()
            downloadName += `_Ending_${maxDateString}`
        } else {
            const minDateString = dayjs(date.minDate).format('YYYYMMDD').toString()
            const maxDateString = dayjs(date.maxDate).format('YYYYMMDD').toString()
    
            downloadName += `_${minDateString}-${maxDateString}`
        }
    }
    const dateString = dayjs(Date.now()).format('YYYYMMDD')
    downloadName += `_Generated_${dateString}`
    return downloadName
}

export default withConfig(({ config }) => {
    const { widgetData: widget } = useContext(WidgetDataContext)
    const { detailKey, currentWidgetGroup } = useContext(CurrentDashboardContext)
    const { authenticatedFetch } = useContext(APIRequestContext)
    const { API_URL } = config
    const { date } = useContext(DateContext)
    const [exporting, setExporting] = useState(false)

    const fetchExportPrefix = useCallback(async () => {
        try {
            var url = widget.DetailKey
                ? `${API_URL}/widget/exportPrefix/${widget.WidgetId}?id=${detailKey}`
                : `${API_URL}/widget/exportPrefix/${widget.WidgetId}`
            return await authenticatedFetch(url)
                .then(async (response) => {
                    if (response.ok) {
                        return await response.text()
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then((response) => {
                    return response
                })
        } catch (e) {
            return null
        }
    }, [widget, detailKey, API_URL])

    const validWidgetColumns = useMemo(() => {
        if (!widget) {
            return {}
        }
        const exampleRow = widget.Data[0] // get the first row of the data to extract cols from
        const hiddenColumns = getHiddenColumns(widget)
        if (!exampleRow) {
            return {}
        }
        return Object.keys(exampleRow)
            .filter((col) => hiddenColumns ? !hiddenColumns.includes(col) : true) // remove hidden columns
            .filter((col) => (widget.SharedKey ? col !== widget.SharedKey : true)) // if there is a shared key, we don't want it to display in the grid
    }, [widget])

    const downloadData = useCallback(async () => {
        if (!exporting) {
            setExporting(true)
            var exportPrefix = await fetchExportPrefix()
            try {
                
                let filteredWidgetData = []
                // convert the data to .csv
                widget.Data.forEach(i => {
                    const filteredObj = Object.keys(i)
                    .filter(key => validWidgetColumns.includes(key))
                    .reduce((obj, key) => {
                        obj[key] = i[key]
                        return obj
                    }, {})
                    filteredWidgetData.push(filteredObj)
                })

                // update the Value label field, if exists
                let exportData = filteredWidgetData
                if (widget.ValueLabel && widget.ValueLabel !== '') {
                    exportData = exportData.map((item) => {
                        item[widget.ValueLabel] = item.Value
                        delete item.Value
                        return item
                    })
                }

                const downloadName = `${constructDownloadName(
                    widget,
                    date,
                    exportPrefix,
                    currentWidgetGroup
                )}.xlsx`
                const wb = XLSX.utils.book_new()
                const ws = XLSX.utils.json_to_sheet(exportData)
                XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
                XLSX.writeFile(wb, downloadName)
                // saveAs(new Blob([csv], { type: 'text/csv' }), downloadName)

            } catch (e) {
                console.log('csv export error:', e)
            }
            setExporting(false)
        }
    }, [widget, date, exporting, fetchExportPrefix, currentWidgetGroup, validWidgetColumns])

    // if there is no widget or no data, do not render this button
    if (!(widget && widget.Data && widget.Data.length)) {
        return null
    }

    // if the widget is not a supported exportable type,
    // then do not render this button
    if (!exportableWidgetTypes.includes(widget.WidgetType)) {
        return null
    }

    const tooltipId = `download-${widget.WidgetId}`
    return (
        <div
            className="exportButton"
            data-tip={'Download Data'}
            data-for={tooltipId}
        >
            {!exporting ? (
                <div
                    className="exportButtonIcon"
                    onClick={() => downloadData()}
                >
                    <FaDownload />
                </div>
            ) : (
                <div className="exportButtonIcon">
                    <IconContext.Provider value={{ className: 'fa-pulse' }}>
                        <FaSpinner />
                    </IconContext.Provider>
                </div>
            )}
            <Tooltip id={tooltipId} place={'bottom'} />
        </div>
    )
})
